import { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import {
    FaStore, FaPlus, FaEdit, FaTrash, FaPlay, FaPause, FaCheckCircle,
    FaExclamationTriangle, FaHourglassHalf, FaMoneyBillWave, FaFileExcel,
    FaEye, FaTimes,
} from 'react-icons/fa';
import apiClient from '../../utils/axiosPetition';
import Cargando from '../../utils/Cargando';
import { alertaGeneral } from '../../utils/alertas';
import VideoTutorialEpayco from '../ePayco/VideoTutorialEpayco';

const formatoPrecio = (valor) => `$${Number(valor || 0).toLocaleString('es-CO')}`;

const EPAYCO_REGISTRO_URL = 'https://epayco.com/registro/';

const formVacio = {
    titulo: '',
    categoryId: '',
    resumen: '',
    descripcion: '',
    precio: '',
    duracion: '',
    modalidad: '',
    metodologia: '',
    objetivos: '',
    contenido: '',
    ciudad: '',
    descuentoPorcentaje: '',
    imagen: null,
};

const estadoBadge = {
    publicado: 'bg-green-100 text-green-700',
    borrador: 'bg-slate-100 text-slate-600',
    pausado: 'bg-amber-100 text-amber-700',
};

/**
 * Panel del profesional en REDPLIKAPLACE:
 * - Onboarding ePayco (escenario 2: crear cuenta propia y reportar el ID)
 * - Gestión de publicaciones (crear, editar, publicar/pausar, eliminar)
 * - Ventas realizadas
 */
const MisProductosPlace = () => {
    const [cargando, setCargando] = useState(true);
    const [estadoVendedor, setEstadoVendedor] = useState(null);
    const [categorias, setCategorias] = useState([]);
    const [publicaciones, setPublicaciones] = useState([]);
    const [ventas, setVentas] = useState([]);
    const [tab, setTab] = useState('productos'); // productos | ventas

    const [modalOpen, setModalOpen] = useState(false);
    const [videoTutorialUrl, setVideoTutorialUrl] = useState('');
    const [editItem, setEditItem] = useState(null);
    const [form, setForm] = useState(formVacio);
    const [guardando, setGuardando] = useState(false);

    const [epaycoIdInput, setEpaycoIdInput] = useState('');
    const [reportando, setReportando] = useState(false);

    const [modalVentasProducto, setModalVentasProducto] = useState(null); // { _id, titulo }

    const cargarTodo = async () => {
        setCargando(true);
        try {
            const [estadoRes, catRes, pubRes] = await Promise.all([
                apiClient.get('/api/marketplace/vendedor/estado'),
                apiClient.get('/api/marketplace/categorias'),
                apiClient.get('/api/marketplace/vendedor/publicaciones'),
            ]);
            setEstadoVendedor(estadoRes.data);
            setCategorias(Array.isArray(catRes.data) ? catRes.data : []);
            setPublicaciones(Array.isArray(pubRes.data) ? pubRes.data : []);
        } catch {
            alertaGeneral({ titulo: 'Error', messageHtml: 'No se pudo cargar tu información de REDPLIKAPLACE', isError: true });
        } finally {
            setCargando(false);
        }
    };

    const cargarVentas = async () => {
        try {
            const res = await apiClient.get('/api/marketplace/vendedor/ventas');
            setVentas(Array.isArray(res.data) ? res.data : []);
            return Array.isArray(res.data) ? res.data : [];
        } catch {
            setVentas([]);
            return [];
        }
    };

    useEffect(() => { cargarTodo(); }, []);

    useEffect(() => {
        if (tab === 'ventas') {
            cargarVentas();
        }
    }, [tab]);

    const ventasPorProducto = useMemo(() => {
        const map = new Map();
        ventas.forEach((v) => {
            const key = String(v.listingId || v.tituloProducto || 'Sin producto');
            if (!map.has(key)) {
                map.set(key, {
                    listingId: v.listingId ? String(v.listingId) : null,
                    producto: v.tituloProducto || 'Sin producto',
                    cantidad: 0,
                    total: 0,
                    recibes: 0,
                });
            }
            const row = map.get(key);
            row.cantidad += 1;
            row.total += Number(v.precio) || 0;
            row.recibes += Number(v.montoVendedor) || 0;
        });
        return Array.from(map.values());
    }, [ventas]);

    const ventasDelModal = useMemo(() => {
        if (!modalVentasProducto) return [];
        const id = String(modalVentasProducto._id);
        const titulo = modalVentasProducto.titulo || '';
        return ventas.filter((v) =>
            (v.listingId && String(v.listingId) === id) ||
            (!v.listingId && v.tituloProducto === titulo)
        );
    }, [modalVentasProducto, ventas]);

    const buildExcelRows = (lista) => lista.map((v) => ({
        Fecha: v.date ? new Date(v.date).toLocaleString('es-CO') : '',
        Producto: v.tituloProducto || '',
        Categoría: v.categoria || '',
        'Nombre comprador': v.nombreComprador || '',
        Correo: v.correoComprador || '',
        Celular: v.celularComprador || '',
        'Tipo ID': v.idTypeComprador || '',
        Identificación: v.idNumberComprador || '',
        'Total pagado': Number(v.precio) || 0,
        'Tu ingreso': Number(v.montoVendedor) || 0,
        Referencia: v.referenceCode || '',
        'Ref ePayco': v.epaycoRef || '',
        Estado: v.estado || 'Aprobado',
    }));

    const exportarVentasExcel = (lista = ventas, nombreArchivo = 'ventas-redplikaplace') => {
        if (!lista.length) {
            return alertaGeneral({ titulo: 'Sin datos', messageHtml: 'No hay ventas para exportar.', isError: true });
        }
        const ws = XLSX.utils.json_to_sheet(buildExcelRows(lista));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Ventas');
        const safe = String(nombreArchivo).replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 60);
        XLSX.writeFile(wb, `${safe}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const abrirVentasProducto = async (producto) => {
        setModalVentasProducto({ _id: producto._id, titulo: producto.titulo });
        if (!ventas.length) {
            await cargarVentas();
        }
    };

    const abrirVideoTutorialEpayco = async (e) => {
        e.preventDefault();
        try {
            const res = await apiClient.get('/api/epayco/tutorial-registro-video-url');
            const url = res.data?.url;
            if (!url) {
                throw new Error('URL vacía');
            }
            setVideoTutorialUrl(url);
        } catch {
            alertaGeneral({
                titulo: 'Video no disponible',
                messageHtml: 'No se pudo abrir el tutorial. Intente desde Configuración de citas o más tarde.',
                isError: true,
            });
        }
    };

    const reportarEpaycoId = async () => {
        if (!epaycoIdInput.trim()) {
            return alertaGeneral({ titulo: 'Error', messageHtml: 'Ingresa el ID de tu cuenta ePayco', isError: true });
        }
        setReportando(true);
        try {
            await apiClient.post('/api/marketplace/vendedor/reportar-epayco', { epaycoAliadoId: epaycoIdInput.trim() });
            Swal.fire('Enviado', 'Tu ID de ePayco fue reportado. El administrador lo confirmará pronto.', 'success');
            setEpaycoIdInput('');
            await cargarTodo();
        } catch (err) {
            alertaGeneral({ titulo: 'Error', messageHtml: err.response?.data?.message || 'No se pudo reportar el ID', isError: true });
        } finally {
            setReportando(false);
        }
    };

    const abrirCrear = () => {
        setEditItem(null);
        setForm({ ...formVacio, categoryId: categorias[0]?._id || categorias[0]?.value || '' });
        setModalOpen(true);
    };

    const abrirEditar = (item) => {
        setEditItem(item);
        setForm({
            titulo: item.titulo || '',
            categoryId: item.category?._id || '',
            resumen: item.resumen || '',
            descripcion: item.descripcion || '',
            precio: item.precio ?? '',
            duracion: item.detalles?.duracion || '',
            modalidad: item.detalles?.modalidad || '',
            metodologia: item.detalles?.metodologia || '',
            objetivos: item.detalles?.objetivos || '',
            contenido: item.detalles?.contenido || '',
            ciudad: item.ciudad || '',
            descuentoPorcentaje: item.descuentoPorcentaje || '',
            imagen: null,
        });
        setModalOpen(true);
    };

    const guardar = async (e) => {
        e.preventDefault();
        if (!form.titulo.trim()) return alertaGeneral({ titulo: 'Error', messageHtml: 'El título es obligatorio', isError: true });
        if (!form.categoryId) return alertaGeneral({ titulo: 'Error', messageHtml: 'Selecciona una categoría', isError: true });
        if (!form.precio || Number(form.precio) < 1000) {
            return alertaGeneral({ titulo: 'Error', messageHtml: 'El precio mínimo es $1.000 COP', isError: true });
        }

        const fd = new FormData();
        fd.append('titulo', form.titulo.trim());
        fd.append('categoryId', form.categoryId);
        fd.append('resumen', form.resumen);
        fd.append('descripcion', form.descripcion);
        fd.append('precio', String(form.precio));
        fd.append('duracion', form.duracion);
        fd.append('modalidad', form.modalidad);
        fd.append('metodologia', form.metodologia);
        fd.append('objetivos', form.objetivos);
        fd.append('contenido', form.contenido);
        fd.append('ciudad', form.ciudad);
        fd.append('descuentoPorcentaje', form.descuentoPorcentaje === '' ? '' : String(form.descuentoPorcentaje));
        if (form.imagen) fd.append('imagen', form.imagen);

        setGuardando(true);
        try {
            if (editItem) {
                await apiClient.put(`/api/marketplace/vendedor/publicaciones/${editItem._id}`, fd);
                Swal.fire('Actualizado', 'La publicación fue actualizada.', 'success');
            } else {
                await apiClient.post('/api/marketplace/vendedor/publicaciones', fd);
                Swal.fire('Creada', 'Tu publicación fue creada como borrador. Publícala cuando esté lista.', 'success');
            }
            setModalOpen(false);
            await cargarTodo();
        } catch (err) {
            const message = err.response?.data?.message || '';
            if (err.response?.status === 401) return;
            if (/invalid token/i.test(message)) {
                alertaGeneral({
                    titulo: 'Sesión expirada',
                    messageHtml: 'Tu sesión ya no es válida. Inicia sesión de nuevo para publicar.',
                    isError: true,
                }).then(() => {
                    localStorage.clear();
                    window.location.href = '/login';
                });
                return;
            }
            alertaGeneral({ titulo: 'Error', messageHtml: message || 'No se pudo guardar la publicación', isError: true });
        } finally {
            setGuardando(false);
        }
    };

    const cambiarEstado = async (item, estado) => {
        try {
            await apiClient.patch(`/api/marketplace/vendedor/publicaciones/${item._id}/estado`, { estado });
            setPublicaciones(prev => prev.map(p => (p._id === item._id ? { ...p, estado } : p)));
        } catch (err) {
            alertaGeneral({ titulo: 'Error', messageHtml: err.response?.data?.message || 'No se pudo cambiar el estado', isError: true });
        }
    };

    const eliminar = async (item) => {
        const result = await Swal.fire({
            title: '¿Eliminar publicación?',
            text: `Se eliminará "${item.titulo}". Esta acción no se puede deshacer.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#dc2626',
        });
        if (!result.isConfirmed) return;
        try {
            await apiClient.delete(`/api/marketplace/vendedor/publicaciones/${item._id}`);
            setPublicaciones(prev => prev.filter(p => p._id !== item._id));
            Swal.fire('Eliminada', 'La publicación fue eliminada.', 'success');
        } catch (err) {
            alertaGeneral({ titulo: 'Error', messageHtml: err.response?.data?.message || 'No se pudo eliminar', isError: true });
        }
    };

    if (cargando) return <Cargando message="Cargando tu espacio en REDPLIKAPLACE..." />;

    const habilitado = estadoVendedor?.habilitadoParaVender;
    const pendienteConfirmacion = estadoVendedor?.tieneEpaycoId && estadoVendedor?.epaycoPendienteConfirmacionAdmin;

    return (
        <div className="w-full py-2 md:py-0">
            {/* Encabezado */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <div className="grid place-items-center w-12 h-12 rounded-xl bg-gradient-to-br from-[#1f94c2] to-[#205087] text-white shadow-lg">
                        <FaStore className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Mis productos</h1>
                        <p className="text-slate-500 text-sm">
                            Crea y ofrece tus cursos, asesorías y servicios en REDPLIKAPLACE.
                        </p>
                    </div>
                </div>
                {habilitado && (
                    <button
                        type="button"
                        onClick={abrirCrear}
                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white bg-[#f79865] hover:bg-[#e88a55] transition-colors shadow-sm"
                    >
                        <FaPlus className="w-4 h-4" /> Nueva publicación
                    </button>
                )}
            </div>

            {/* Estado ePayco */}
            {habilitado ? (
                <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                    <FaCheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-green-800 font-semibold">
                        Cuenta ePayco activa — Puedes publicar y recibir pagos.
                    </p>
                </div>
            ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
                    <div className={`flex items-start gap-3 ${pendienteConfirmacion ? '' : 'mb-4'}`}>
                        {pendienteConfirmacion ? (
                            <FaHourglassHalf className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                        ) : (
                            <FaExclamationTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                        )}
                        <div className="text-sm text-amber-800">
                            <p className="font-semibold mb-1">
                                {pendienteConfirmacion
                                    ? 'Tu cuenta de ePayco está en proceso de validación.'
                                    : 'Configura tu cuenta ePayco para vender'}
                            </p>
                            <p>
                                {pendienteConfirmacion
                                    ? 'Ya reportaste tu ID. Cuando el administrador lo confirme, se habilitará el botón para crear publicaciones.'
                                    : 'Para vender en REDPLIKAPLACE necesitas una cuenta propia en ePayco. Cuando la reportes y el admin la confirme, podrás crear publicaciones.'}
                            </p>
                        </div>
                    </div>

                    {!pendienteConfirmacion && (
                        <ol className="space-y-4 ml-1">
                            <li className="flex gap-3">
                                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#205087] text-white font-semibold flex items-center justify-center text-xs">1</span>
                                <div className="text-sm text-amber-900">
                                    <p className="font-semibold text-slate-800">Crea tu cuenta en ePayco</p>
                                    <p className="mt-0.5">
                                        El registro es gratuito.{' '}
                                        <a
                                            href={EPAYCO_REGISTRO_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-semibold text-[#205087] hover:underline"
                                        >
                                            Registrarse en ePayco →
                                        </a>
                                    </p>
                                </div>
                            </li>
                            <li className="flex gap-3">
                                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#205087] text-white font-semibold flex items-center justify-center text-xs">2</span>
                                <div className="text-sm text-amber-900">
                                    <p className="font-semibold text-slate-800">Completa el registro</p>
                                    <p className="mt-0.5">
                                        Sigue el tutorial paso a paso.{' '}
                                        <button
                                            type="button"
                                            onClick={abrirVideoTutorialEpayco}
                                            className="font-semibold text-[#205087] hover:underline"
                                        >
                                            Ver tutorial →
                                        </button>
                                    </p>
                                </div>
                            </li>
                            <li className="flex gap-3">
                                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#205087] text-white font-semibold flex items-center justify-center text-xs">3</span>
                                <div className="text-sm text-amber-900 flex-1 min-w-0">
                                    <p className="font-semibold text-slate-800">Reporta tu ID a REDPLIKA</p>
                                    <p className="mt-0.5">Ingresa en la plataforma el ID que ePayco te asignó.</p>
                                    <div className="flex flex-col sm:flex-row gap-2 mt-2">
                                        <input
                                            type="text"
                                            value={epaycoIdInput}
                                            onChange={(e) => setEpaycoIdInput(e.target.value)}
                                            placeholder="ID de tu cuenta ePayco"
                                            className="flex-1 border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#1f94c2]"
                                        />
                                        <button
                                            type="button"
                                            onClick={reportarEpaycoId}
                                            disabled={reportando}
                                            className="px-5 py-2 rounded-lg font-semibold text-white bg-[#205087] hover:bg-[#1f94c2] transition-colors disabled:opacity-60"
                                        >
                                            {reportando ? 'Enviando...' : 'Reportar ID'}
                                        </button>
                                    </div>
                                </div>
                            </li>
                            <li className="flex gap-3">
                                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#205087] text-white font-semibold flex items-center justify-center text-xs">4</span>
                                <div className="text-sm text-amber-900">
                                    <p className="font-semibold text-slate-800">Publica tus productos</p>
                                    <p className="mt-0.5">Una vez validada tu cuenta, crea y publica tus servicios.</p>
                                </div>
                            </li>
                        </ol>
                    )}
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2 mb-5">
                <button
                    type="button"
                    onClick={() => setTab('productos')}
                    className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === 'productos' ? 'bg-[#205087] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-[#1f94c2]'}`}
                >
                    Mis publicaciones ({publicaciones.length})
                </button>
                <button
                    type="button"
                    onClick={() => setTab('ventas')}
                    className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === 'ventas' ? 'bg-[#205087] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-[#1f94c2]'}`}
                >
                    Mis ventas{ventas.length ? ` (${ventas.length})` : ''}
                </button>
            </div>

            {tab === 'productos' && (
                publicaciones.length === 0 ? (
                    <div className="text-center py-14 bg-white rounded-2xl border border-slate-200">
                        <FaStore className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-600 font-medium">
                            {habilitado
                                ? 'Aún no tienes publicaciones. Crea tu primer curso o asesoría con el botón "Nueva publicación".'
                                : pendienteConfirmacion
                                    ? 'Aún no tienes publicaciones. Espera la confirmación del administrador para empezar a publicar.'
                                    : 'Aún no tienes publicaciones. Primero configura y reporta tu ID de ePayco. Después de la verificación del admin podrás publicar.'}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {publicaciones.map((item) => (
                            <div key={item._id} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center gap-4">
                                <div className="w-full sm:w-24 h-20 rounded-lg bg-slate-100 overflow-hidden shrink-0">
                                    {item.imagenPrincipal ? (
                                        <img src={item.imagenPrincipal} alt={item.titulo} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                                            <FaStore className="w-6 h-6" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <h3 className="font-bold text-slate-800 truncate">{item.titulo}</h3>
                                        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${estadoBadge[item.estado] || estadoBadge.borrador}`}>
                                            {item.estado}
                                        </span>
                                        {item.category && (
                                            <span className="text-xs text-slate-500">{item.category.label}</span>
                                        )}
                                    </div>
                                    <p className="text-sm text-slate-500 truncate">{item.resumen || 'Sin descripción breve'}</p>
                                    <p className="text-sm font-semibold text-[#205087] mt-1">
                                        {formatoPrecio(item.precio)} COP
                                        <span className="text-xs text-slate-400 font-normal ml-3">
                                            {item.ventas || 0} venta(s) · {item.vistas || 0} vista(s)
                                        </span>
                                    </p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                                    <button
                                        type="button"
                                        title="Ver ventas de este producto"
                                        onClick={() => abrirVentasProducto(item)}
                                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#205087] text-white text-xs font-semibold hover:bg-[#1f94c2] transition-colors"
                                    >
                                        <FaEye className="w-3.5 h-3.5" />
                                        Ver ventas ({item.ventas || 0})
                                    </button>
                                    {item.estado === 'publicado' ? (
                                        <button
                                            type="button"
                                            title="Pausar"
                                            onClick={() => cambiarEstado(item, 'pausado')}
                                            className="p-2.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors"
                                        >
                                            <FaPause className="w-4 h-4" />
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            title="Publicar"
                                            onClick={() => cambiarEstado(item, 'publicado')}
                                            className="p-2.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
                                        >
                                            <FaPlay className="w-4 h-4" />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        title="Editar"
                                        onClick={() => abrirEditar(item)}
                                        className="p-2.5 rounded-lg bg-blue-50 text-[#205087] hover:bg-blue-100 transition-colors"
                                    >
                                        <FaEdit className="w-4 h-4" />
                                    </button>
                                    <button
                                        type="button"
                                        title="Eliminar"
                                        onClick={() => eliminar(item)}
                                        className="p-2.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                                    >
                                        <FaTrash className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )
            )}

            {tab === 'ventas' && (
                ventas.length === 0 ? (
                    <div className="text-center py-14 bg-white rounded-2xl border border-slate-200">
                        <FaMoneyBillWave className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-600 font-medium">Aún no tienes ventas registradas</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <p className="text-sm text-slate-600">
                                {ventas.length} venta(s) · datos de contacto del comprador para que puedas contactarlo.
                            </p>
                            <button
                                type="button"
                                onClick={() => exportarVentasExcel(ventas, 'ventas-redplikaplace-todas')}
                                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-green-600 hover:bg-green-700 transition-colors"
                            >
                                <FaFileExcel className="w-4 h-4" /> Descargar Excel (todas)
                            </button>
                        </div>

                        {ventasPorProducto.length > 0 && (
                            <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-slate-50 text-left text-slate-600">
                                            <th className="px-4 py-3 font-semibold">Resumen por producto</th>
                                            <th className="px-4 py-3 font-semibold text-right">Ventas</th>
                                            <th className="px-4 py-3 font-semibold text-right">Total</th>
                                            <th className="px-4 py-3 font-semibold text-right">Recibes</th>
                                            <th className="px-4 py-3 font-semibold text-right">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ventasPorProducto.map((r) => (
                                            <tr key={r.listingId || r.producto} className="border-t border-slate-100">
                                                <td className="px-4 py-3 font-medium text-slate-700">{r.producto}</td>
                                                <td className="px-4 py-3 text-right text-slate-600">{r.cantidad}</td>
                                                <td className="px-4 py-3 text-right text-slate-700">{formatoPrecio(r.total)}</td>
                                                <td className="px-4 py-3 text-right font-semibold text-green-600">{formatoPrecio(r.recibes)}</td>
                                                <td className="px-4 py-3 text-right">
                                                    <div className="inline-flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => abrirVentasProducto({
                                                                _id: r.listingId || publicaciones.find((p) => p.titulo === r.producto)?._id,
                                                                titulo: r.producto,
                                                            })}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-[#205087] text-white hover:bg-[#1f94c2]"
                                                        >
                                                            <FaEye className="w-3 h-3" /> Ver
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const lista = ventas.filter((v) =>
                                                                    (r.listingId && String(v.listingId) === String(r.listingId)) ||
                                                                    (!r.listingId && v.tituloProducto === r.producto)
                                                                );
                                                                exportarVentasExcel(lista, `ventas_${r.producto}`);
                                                            }}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-green-600 text-white hover:bg-green-700"
                                                        >
                                                            <FaFileExcel className="w-3 h-3" /> Excel
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-slate-50 text-left text-slate-600">
                                        <th className="px-4 py-3 font-semibold">Fecha</th>
                                        <th className="px-4 py-3 font-semibold">Producto</th>
                                        <th className="px-4 py-3 font-semibold">Comprador</th>
                                        <th className="px-4 py-3 font-semibold">Correo</th>
                                        <th className="px-4 py-3 font-semibold">Celular</th>
                                        <th className="px-4 py-3 font-semibold">Documento</th>
                                        <th className="px-4 py-3 font-semibold text-right">Total</th>
                                        <th className="px-4 py-3 font-semibold text-right">Recibes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ventas.map((v) => (
                                        <tr key={v._id} className="border-t border-slate-100">
                                            <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                                                {v.date ? new Date(v.date).toLocaleDateString('es-CO') : '—'}
                                            </td>
                                            <td className="px-4 py-3 text-slate-700 font-medium min-w-[160px]">{v.tituloProducto}</td>
                                            <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{v.nombreComprador || '—'}</td>
                                            <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                                                {v.correoComprador ? (
                                                    <a href={`mailto:${v.correoComprador}`} className="text-[#205087] hover:underline">
                                                        {v.correoComprador}
                                                    </a>
                                                ) : '—'}
                                            </td>
                                            <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                                                {v.celularComprador ? (
                                                    <a href={`https://wa.me/57${String(v.celularComprador).replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="text-[#205087] hover:underline">
                                                        {v.celularComprador}
                                                    </a>
                                                ) : '—'}
                                            </td>
                                            <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                                                {[v.idTypeComprador, v.idNumberComprador].filter(Boolean).join(' ') || '—'}
                                            </td>
                                            <td className="px-4 py-3 text-right text-slate-700 whitespace-nowrap">{formatoPrecio(v.precio)}</td>
                                            <td className="px-4 py-3 text-right font-semibold text-green-600 whitespace-nowrap">{formatoPrecio(v.montoVendedor)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )
            )}

            {/* Modal crear/editar */}
            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <form onSubmit={guardar} className="p-6">
                            <h2 className="text-lg font-bold text-slate-800 mb-5">
                                {editItem ? 'Editar publicación' : 'Nueva publicación'}
                            </h2>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-semibold text-slate-600 mb-1">Título *</label>
                                    <input
                                        type="text"
                                        value={form.titulo}
                                        onChange={(e) => setForm(f => ({ ...f, titulo: e.target.value }))}
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                                        placeholder="Ej: Curso de primeros auxilios para cuidadores"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-600 mb-1">Categoría *</label>
                                    <select
                                        value={form.categoryId}
                                        onChange={(e) => setForm(f => ({ ...f, categoryId: e.target.value }))}
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                                    >
                                        <option value="">Selecciona</option>
                                        {categorias.map(c => (
                                            <option key={c._id || c.value} value={c._id}>{c.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-600 mb-1">Precio (COP) *</label>
                                    <input
                                        type="number"
                                        min="1000"
                                        value={form.precio}
                                        onChange={(e) => setForm(f => ({ ...f, precio: e.target.value }))}
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                                        placeholder="Ej: 90000"
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-semibold text-slate-600 mb-1">Descripción breve (card del listado)</label>
                                    <input
                                        type="text"
                                        maxLength={300}
                                        value={form.resumen}
                                        onChange={(e) => setForm(f => ({ ...f, resumen: e.target.value }))}
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                                        placeholder="Resumen corto que verán los compradores"
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-semibold text-slate-600 mb-1">Descripción completa</label>
                                    <textarea
                                        rows={4}
                                        value={form.descripcion}
                                        onChange={(e) => setForm(f => ({ ...f, descripcion: e.target.value }))}
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                                        placeholder="Describe tu producto o servicio en detalle"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-600 mb-1">Duración</label>
                                    <input
                                        type="text"
                                        value={form.duracion}
                                        onChange={(e) => setForm(f => ({ ...f, duracion: e.target.value }))}
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                                        placeholder="Ej: 8 horas / 4 sesiones"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-600 mb-1">Modalidad</label>
                                    <select
                                        value={form.modalidad}
                                        onChange={(e) => setForm(f => ({ ...f, modalidad: e.target.value }))}
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                                    >
                                        <option value="">Selecciona</option>
                                        <option value="Virtual">Virtual</option>
                                        <option value="Presencial">Presencial</option>
                                        <option value="Mixta">Mixta</option>
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-semibold text-slate-600 mb-1">Objetivos</label>
                                    <textarea
                                        rows={2}
                                        value={form.objetivos}
                                        onChange={(e) => setForm(f => ({ ...f, objetivos: e.target.value }))}
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-semibold text-slate-600 mb-1">Contenido / temario</label>
                                    <textarea
                                        rows={3}
                                        value={form.contenido}
                                        onChange={(e) => setForm(f => ({ ...f, contenido: e.target.value }))}
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-semibold text-slate-600 mb-1">Metodología</label>
                                    <textarea
                                        rows={2}
                                        value={form.metodologia}
                                        onChange={(e) => setForm(f => ({ ...f, metodologia: e.target.value }))}
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-600 mb-1">Ciudad (si es presencial/mixta)</label>
                                    <input
                                        type="text"
                                        value={form.ciudad}
                                        onChange={(e) => setForm(f => ({ ...f, ciudad: e.target.value }))}
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                                        placeholder="Ej: Bogotá"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-600 mb-1">Promoción: % de descuento</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="90"
                                        value={form.descuentoPorcentaje}
                                        onChange={(e) => setForm(f => ({ ...f, descuentoPorcentaje: e.target.value }))}
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                                        placeholder="0 = sin descuento"
                                    />
                                    {Number(form.descuentoPorcentaje) > 0 && Number(form.precio) >= 1000 && (
                                        <p className="text-xs text-green-600 mt-1">
                                            Precio final: {formatoPrecio(Math.round(Number(form.precio) * (1 - Number(form.descuentoPorcentaje) / 100)))} COP
                                        </p>
                                    )}
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-semibold text-slate-600 mb-1">
                                        Imagen principal {editItem?.imagenPrincipal ? '(dejar vacío para conservar la actual)' : ''}
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setForm(f => ({ ...f, imagen: e.target.files?.[0] || null }))}
                                        className="w-full text-sm text-slate-600"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-3 justify-end">
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(false)}
                                    className="px-5 py-2 rounded-lg font-semibold text-slate-600 border border-slate-300 hover:bg-slate-50 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={guardando}
                                    className="px-5 py-2 rounded-lg font-semibold text-white bg-[#205087] hover:bg-[#1f94c2] transition-colors disabled:opacity-60"
                                >
                                    {guardando ? 'Guardando...' : editItem ? 'Guardar cambios' : 'Crear publicación'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal ventas por producto */}
            {modalVentasProducto && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100">
                            <div className="min-w-0">
                                <h2 className="text-lg font-bold text-slate-800 truncate">
                                    Ventas — {modalVentasProducto.titulo}
                                </h2>
                                <p className="text-sm text-slate-500 mt-0.5">
                                    {ventasDelModal.length} venta(s) de este producto · datos de contacto del comprador
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setModalVentasProducto(null)}
                                className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                                aria-label="Cerrar"
                            >
                                <FaTimes className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="px-5 py-3 flex flex-wrap gap-2 border-b border-slate-50">
                            <button
                                type="button"
                                onClick={() => exportarVentasExcel(
                                    ventasDelModal,
                                    `ventas_${modalVentasProducto.titulo || 'producto'}`
                                )}
                                disabled={!ventasDelModal.length}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-colors"
                            >
                                <FaFileExcel className="w-4 h-4" /> Descargar Excel de este producto
                            </button>
                        </div>

                        <div className="flex-1 overflow-auto p-5">
                            {ventasDelModal.length === 0 ? (
                                <div className="text-center py-12 text-slate-500">
                                    <FaMoneyBillWave className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                                    Este producto aún no tiene ventas registradas.
                                </div>
                            ) : (
                                <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-slate-50 text-left text-slate-600">
                                                <th className="px-4 py-3 font-semibold">Fecha</th>
                                                <th className="px-4 py-3 font-semibold">Comprador</th>
                                                <th className="px-4 py-3 font-semibold">Correo</th>
                                                <th className="px-4 py-3 font-semibold">Celular</th>
                                                <th className="px-4 py-3 font-semibold">Documento</th>
                                                <th className="px-4 py-3 font-semibold text-right">Total</th>
                                                <th className="px-4 py-3 font-semibold text-right">Recibes</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ventasDelModal.map((v) => (
                                                <tr key={v._id} className="border-t border-slate-100">
                                                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                                                        {v.date ? new Date(v.date).toLocaleDateString('es-CO') : '—'}
                                                    </td>
                                                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{v.nombreComprador || '—'}</td>
                                                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                                                        {v.correoComprador ? (
                                                            <a href={`mailto:${v.correoComprador}`} className="text-[#205087] hover:underline">
                                                                {v.correoComprador}
                                                            </a>
                                                        ) : '—'}
                                                    </td>
                                                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                                                        {v.celularComprador || '—'}
                                                    </td>
                                                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                                                        {[v.idTypeComprador, v.idNumberComprador].filter(Boolean).join(' ') || '—'}
                                                    </td>
                                                    <td className="px-4 py-3 text-right text-slate-700 whitespace-nowrap">{formatoPrecio(v.precio)}</td>
                                                    <td className="px-4 py-3 text-right font-semibold text-green-600 whitespace-nowrap">{formatoPrecio(v.montoVendedor)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {videoTutorialUrl && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4" onContextMenu={(event) => event.preventDefault()}>
                    <div className="w-full max-w-3xl">
                        <div className="mb-2 flex items-center justify-between text-white">
                            <p className="text-sm font-semibold">Tutorial de registro en ePayco</p>
                            <button
                                type="button"
                                onClick={() => setVideoTutorialUrl('')}
                                className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-white/10"
                            >
                                <FaTimes className="h-3.5 w-3.5" /> Cerrar
                            </button>
                        </div>
                        <div className="aspect-video overflow-hidden rounded-xl bg-black">
                            <VideoTutorialEpayco src={videoTutorialUrl} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MisProductosPlace;
